"use client";

import React, { useEffect, useRef, useState } from "react"; // useRef kept for AnimatedCounter
import SectionHeader from "./section-header";
import { testimonials, testimonialStats } from "@/lib/data";
import Image from "next/image";

const AnimatedCounter = ({ targetString }: { targetString: string }) => {
  // Track when the component enters the visible screen area
  const [isVisible, setIsVisible] = useState(false);
  // Store the current animated number being displayed
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);

  // Analyze the target string (e.g., "98%" or "10K+") to determine required suffixes
  const isPercentage = targetString.includes("%");
  const isKPlus = targetString.toLowerCase().includes("k+");

  // Extract only the digits from the string (e.g., "10K+" becomes 10)
  const targetNumber = parseInt(targetString.replace(/\D/g, ""), 10);
  // For "K+" styles, start counting from 1 (e.g., 1K to 10K). Otherwise, start at 0.
  const startNumber = isKPlus ? 1 : 0;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Use an observer to only trigger the animation when the user scrolls 50% of the element into view
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // If not visible yet, freeze the number at the starting baseline
    if (!isVisible) {
      setCount(startNumber);
      return;
    }

    // Keep track of the exact millisecond the animation begins
    let start: number | null = null;
    // Total animation runtime defined in milliseconds (2 seconds)
    const duration = 2000;

    // A high-performance recursive step function running at the monitor's native refresh rate (e.g., 60fps)
    const step = (timestamp: number) => {
      if (!start) start = timestamp;

      // Calculate how many milliseconds have passed since the animation started
      const progress = timestamp - start;
      // Convert progress to a 0 to 1 completion ratio
      const ratio = Math.min(progress / duration, 1);

      // Apply an "easeOutQuad" curve which decelerates smoothly without skipping initial digit frames
      const easeOut = ratio * (2 - ratio);

      // Multiply the ease ratio against the total distance to travel, and add the baseline
      const currentCount = Math.floor(
        easeOut * (targetNumber - startNumber) + startNumber,
      );
      setCount(currentCount);

      // If the duration hasn't expired, recursively call the next visual frame
      if (progress < duration) {
        window.requestAnimationFrame(step);
      } else {
        // Execution safety: forcefully set bounds to target upon completion
        setCount(targetNumber);
      }
    };

    // Kickstart the first animation frame loop
    window.requestAnimationFrame(step);
  }, [isVisible, targetNumber, startNumber]);

  return (
    <span ref={ref}>
      {count}
      {/* Conditionally re-attach the correct static suffix originally provided */}
      {isPercentage ? "%" : isKPlus ? "K+" : ""}
    </span>
  );
};

const TestimonialCard = ({
  testimonial,
  index,
}: {
  testimonial: (typeof testimonials)[number];
  index: number;
}) => {
  return (
    <div className={`${index >= testimonials.length - 2 ? "hidden sm:flex" : "flex"} flex-col sm:flex-row items-stretch w-full leading-6 outline outline-surface-light rounded-[20px] overflow-hidden sm:overflow-auto sm:rounded-none sm:outline-none shadow-[0px_0px_28px_0px_#E2E2E2B2] sm:shadow-none relative`}>
      {/* Image pane */}
      <div
        className={`relative w-full sm:basis-1/2 h-50 sm:h-full ${index < 2 ? "lg:order-1" : "lg:order-2"} ${
          index % 2 === 0 ? "sm:order-1" : "sm:order-2"
        }`}
      >
        <Image
          src={testimonial.image}
          alt=""
          fill
          className="object-cover object-center"
        />
        <div
          className={`hidden sm:block absolute top-1/2 -translate-y-1/2 z-10 w-0 h-0 border-t-20 xl:border-t-24 border-t-transparent border-b-20 xl:border-b-24 border-b-transparent border-r-26 xl:border-r-32 border-surface-white ${index < 2 ? "right-0" : "left-0 rotate-180"}`}
        />
      </div>

      {/* Text pane */}
      <div
        className={`sm:basis-1/2 py-5 sm:pt-10 xl:pt-16.5 sm:pb-2 sm:min-h-62 lg:min-h-70 xl:min-h-85 px-3 sm:px-5.5 xl:px-7.5 bg-surface-white flex-1 space-y-6 text-xs xl:text-sm leading-6 sm:leading-5 xl:leading-6 ${index < 2 ? "lg:order-2" : "lg:order-1"} ${
          index % 2 === 0 ? "sm:order-2" : "sm:order-1"
        }`}
      >
        <div className="space-y-2 sm:space-y-2.5 xl:space-y-4">
          <div className="flex gap-x-0.5 lg:gap-x-1">
            {Array.from({ length: testimonial.rating }).map((_, i) => (
              <svg
                key={i * 4}
                xmlns="http://www.w3.org/2000/svg"
                className="size-5.5 sm:size-5 xl:size-6"
                viewBox="0 0 24 24"
                fill="#FFA517"
                stroke="none"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
              </svg>
            ))}
          </div>

          <p className="text-surface">{testimonial.review}</p>
        </div>

        <div className="sm:space-y-1 text-surface-black">
          <p className="font-semibold">{testimonial.name}</p>
          <p>{testimonial.detail}</p>
        </div>
      </div>
    </div>
  );
};

const Testimonial = () => {
  return (
    <section
      className="2xl:container 2xl:mx-auto 2xl:px-4 sm:px-4 space-y-6 xl:space-y-8"
      id="testimonials"
    >
      <div className="space-y-8 xl:space-y-12">
        <div className="space-y-5 md:space-y-6 lg:space-y-8 xl:space-y-12 leading-none">
          <SectionHeader title="testimonials" leftAlign={false} />
          <h2 className="text-[21px] sm:text-[28px] lg:text-[37px] xl:text-5xl font-semibold text-surface-black text-center">
            Hear from Nigerian Mothers
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-0 px-4 md:px-7.5 xl:px-[36.25px]">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={testimonial.name}
              testimonial={testimonial}
              index={index}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center gap-x-4 md:gap-x-6 lg:gap-x-8 xl:gap-x-12 text-center">
        {testimonialStats.map((stat) => (
          <div key={stat.label} className="lg:space-y-1.25">
            <p className="text-[21px] sm:text-[28px] lg:text-[37px] xl:text-5xl leading-none text-primary font-semibold">
              <AnimatedCounter targetString={stat.stat} />
            </p>

            <span className="text-surface text-[13px] sm:text-xs lg:text-sm xl:text-base leading-none">
              {stat.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Testimonial;
